import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { apiClient } from '../services/api';
import { Modal } from './Modal';
import { PenkiChip } from './PenkiBadge';
import { useUser } from '../contexts/UserContext';
import { formatPenki } from '../utils/penki';
import type { ShopProduct, ShopPurchase } from '../types';
import './ShopProductPage.css';
import './NFTDetailPage.css';
import './NFTDetailModal.css';

function purchaseToken(productId: number): string {
  const key = `shop_purchase_${productId}`;
  let token = sessionStorage.getItem(key);
  if (!token) {
    token = crypto.randomUUID();
    sessionStorage.setItem(key, token);
  }
  return token;
}

export const ShopProductPage: React.FC<{ owned?: boolean }> = ({ owned = false }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, refreshUser } = useUser();
  const numericId = Number(id);

  const [product, setProduct] = useState<ShopProduct | null>(null);
  const [purchase, setPurchase] = useState<ShopPurchase | null>(null);
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [notEnoughOpen, setNotEnoughOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!numericId) {
        setError('Товар не найден');
        setLoading(false);
        return;
      }
      try {
        if (owned) {
          const data = await apiClient.getShopPurchase(numericId);
          if (!cancelled) {
            setPurchase(data);
            setProduct(data.product ? {
              id: data.product.id,
              title: data.product.title,
              image_url: data.product.image_url,
              price_penki: data.price_penki,
              fulfillment_type: data.product.fulfillment_type,
              fulfillment_note: data.fulfillment_note,
              available: false,
            } : null);
          }
        } else {
          const data = await apiClient.getShopProduct(numericId);
          if (!cancelled) setProduct(data);
        }
      } catch (err) {
        console.error(err);
        if (!cancelled) setError('Не удалось загрузить товар');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [numericId, owned]);

  const handleBuy = async () => {
    if (!product || pending) return;
    setPending(true);
    setError(null);
    try {
      const result = await apiClient.purchaseShopProduct(product.id, purchaseToken(product.id));
      sessionStorage.removeItem(`shop_purchase_${product.id}`);
      setPurchase(result);
      await refreshUser();
      setSuccessOpen(true);
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.data?.error === 'insufficient_penki') {
        setNotEnoughOpen(true);
      } else if (axios.isAxiosError(err) && err.response?.data?.error === 'out_of_stock') {
        setError('Товар закончился');
      } else if (axios.isAxiosError(err) && err.response?.data?.error === 'inactive') {
        setError('Товар недоступен');
      } else {
        setError('Не удалось купить товар');
      }
    } finally {
      setPending(false);
    }
  };

  const handleCopy = async () => {
    if (!purchase?.code) return;
    try {
      await navigator.clipboard.writeText(purchase.code);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      // ignore
    }
  };

  if (loading) {
    return (
      <div className="nft-detail-page">
        <div className="nft-detail-loading">
          <div className="nft-detail-spinner" />
        </div>
      </div>
    );
  }

  if (error && !product) {
    return (
      <div className="nft-detail-page">
        <div className="nft-detail-error">
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (!product) {
    return null;
  }

  const showOwned = owned || Boolean(purchase);
  const available = user?.penki_balance ?? 0;
  const missing = Math.max(0, product.price_penki - available);

  return (
    <div className="nft-detail-page">
      <div className="nft-detail-image-container">
        {product.image_url ? (
          <img src={product.image_url} alt={product.title} className="nft-detail-image loaded" />
        ) : (
          <div className="catalog-nft-placeholder shop-card-art" style={{ height: '100%' }} />
        )}
        <div className="nft-detail-image-overlays">
          <button
            type="button"
            className="nft-detail-overlay-btn"
            onClick={() => navigate(-1)}
            aria-label="Назад"
          >
            <span className="nft-detail-overlay-circle">
              <img src="/back.png" alt="" className="nft-detail-overlay-icon" />
            </span>
          </button>
        </div>
      </div>

      <div className="nft-detail-info-block">
        <div className="nft-detail-info-row">
          <h2 className="nft-detail-title">{product.title}</h2>
        </div>
        {showOwned && purchase?.status && (
          <p className="nft-detail-owner-line">{purchase.status_label}</p>
        )}
        {showOwned && purchase?.fulfillment_type === 'auto' && purchase.code && (
          <div className="product-promocode">
            <span className="product-promocode-text">Промокод: {purchase.code}</span>
            <button
              type="button"
              className="product-promocode-copy"
              onClick={handleCopy}
              aria-label="Скопировать промокод"
            >
              {copied ? (
                '✓'
              ) : (
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" aria-hidden="true">
                  <rect x="9" y="9" width="11" height="11" rx="2.5" stroke="currentColor" strokeWidth="1.8" />
                  <path
                    d="M15 6.5A2.5 2.5 0 0012.5 4H6.5A2.5 2.5 0 004 6.5v6A2.5 2.5 0 006.5 15"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                  />
                </svg>
              )}
            </button>
          </div>
        )}
        {showOwned && purchase?.fulfillment_type === 'manual' && (
          <p className="nft-detail-description">{purchase.fulfillment_note}</p>
        )}
        {error && <p className="nft-detail-description">{error}</p>}
      </div>

      {!showOwned && (
        <div className="nft-detail-actions">
          <button
            type="button"
            className="button--accent button--full"
            onClick={handleBuy}
            disabled={pending || !product.available}
          >
            {pending
              ? 'Покупаем…'
              : product.available
                ? `Купить за ${formatPenki(product.price_penki)} УЙМ`
                : 'Нет в наличии'}
          </button>
        </div>
      )}

      {showOwned && purchase?.fulfillment_type === 'manual' && (
        <div className="nft-detail-actions">
          <button
            type="button"
            className="button--primary button--full"
            onClick={() => navigate('/support')}
          >
            Получить товар
          </button>
        </div>
      )}

      <Modal isOpen={successOpen} onClose={() => setSuccessOpen(false)} title="Успешно!">
        <p className="shop-modal-subtitle">Товар доступен в разделе «Мои покупки»</p>
        <button
          type="button"
          className="button--primary button--full"
          onClick={() => {
            setSuccessOpen(false);
            navigate('/shop?tab=purchased');
          }}
        >
          Смотреть
        </button>
      </Modal>

      <Modal isOpen={notEnoughOpen} onClose={() => setNotEnoughOpen(false)} title="Не хватает УЙМ">
        <p className="shop-modal-subtitle">Купи больше ху#ков, чтобы заработать УЙМЫ</p>
        <div className="not-enough-rows">
          <div className="not-enough-row">
            <span className="not-enough-row-label">Нужно УЙМ:</span>
            <PenkiChip value={product.price_penki} bare />
          </div>
          <div className="not-enough-row not-enough-row--divided">
            <span className="not-enough-row-label">У тебя сейчас:</span>
            <PenkiChip value={available} bare />
          </div>
          <div className="not-enough-row">
            <span className="not-enough-row-label">Не хватает:</span>
            <PenkiChip value={missing} bare danger />
          </div>
        </div>
        <button
          type="button"
          className="button--primary button--full"
          onClick={() => {
            setNotEnoughOpen(false);
            navigate('/');
          }}
        >
          Купить ху#ки
        </button>
      </Modal>
    </div>
  );
};
